import type { ResultColumn, ResultEnvelope, Row } from "@cd/core";

export interface PivotModel {
  rowDims: ResultColumn[];
  colDim: ResultColumn;
  metric: ResultColumn;
  /** [key, first row of that group], in result order. */
  rowKeys: [string, Row][];
  /** [key, column value], sorted naturally. */
  colKeys: [string, Row[string]][];
  /** `${rowKey}|${colKey}` → value. */
  cells: Map<string, number | null>;
}

/** Rows = all groupings but the last; columns = the last grouping; cells = first metric. */
export function buildPivot(result: ResultEnvelope): PivotModel {
  const dims = result.columns.filter((c) => c.kind === "dimension");
  const metric = result.columns.find((c) => c.kind === "metric")!;
  const rowDims = dims.slice(0, -1);
  const colDim = dims.at(-1)!;
  const rowKeys = new Map<string, Row>();
  const colKeys = new Map<string, Row[string]>();
  const cells = new Map<string, number | null>();
  for (const r of result.rows) {
    const rk = JSON.stringify(rowDims.map((d) => r[d.key]));
    const ck = JSON.stringify(r[colDim.key]);
    if (!rowKeys.has(rk)) rowKeys.set(rk, r);
    if (!colKeys.has(ck)) colKeys.set(ck, r[colDim.key] ?? null);
    cells.set(`${rk}|${ck}`, r[metric.key] as number | null);
  }
  const sortedCols = [...colKeys.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1]), undefined, { numeric: true }));
  return { rowDims, colDim, metric, rowKeys: [...rowKeys.entries()], colKeys: sortedCols, cells };
}
